import { useMemo, useState } from "react";
import { DatePicker } from "@/components/ui/date-picker";
import {
  BellRing, MessageSquareText, Plus, Search, Send, TriangleAlert, UserRoundSearch, WalletCards, Star, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { useCrm } from "@/hooks/useCrm";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageShell } from "@/components/PageShell";
import { MONEY_CAPTION, MONEY_LINE, inr } from "@/lib/format";
import { EmptyState } from "@/components/ui/StateBoundaries";

const tabs = [
  ["profiles", "Customer profiles", UserRoundSearch],
  ["khata", "Khata & corporate", WalletCards],
  ["segments", "Segments & campaigns", MessageSquareText],
  ["feedback", "Feedback loop", BellRing],
];

const SEGMENTS = [
  ["all", "Everyone with a phone"],
  ["loyalty", "Has loyalty points"],
  ["lapsed", "Not seen in 30 days"],
  ["birthday", "Birthday in the next week"],
];

// The local formatter that stood here printed a hardcoded ₹ with no fraction
// digits, so a ₹450 balance and a ₹449.50 one sat at different widths in the same
// khata column. inr() is the product's one money formatter (§4.2).
const initials = name => String(name || "?").slice(0, 2).toUpperCase();

// ---------------------------------------------------------------- dialogs

function CustomerDialog({ tiers, onSave, onClose }) {
  const [form, setForm] = useState({ name: "", phone: "", dob: "", tier_id: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(c => ({ ...c, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!/^\d{10}$/.test(form.phone)) return toast.error("Phone must be 10 digits");
    setSaving(true);
    const result = await onSave(
      { name: form.name.trim(), phone: form.phone, dob: form.dob || null },
      form.tier_id ? Number(form.tier_id) : null
    );
    setSaving(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(`${form.name} added`);
    onClose();
  };
  return <Modal title="Add customer" onClose={onClose} testId="add-customer-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-customer-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-customer-button">Add customer</Button></>}>
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Name</label>
        <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="customer-name-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Phone</label>
        <Input inputMode="numeric" value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="10 digits" data-testid="customer-phone-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="block text-meta font-medium text-muted-foreground">Date of birth</label>
        <DatePicker value={form.dob} onChange={next => set("dob", next)} max={new Date().toISOString().slice(0, 10)} ariaLabel="Date of birth" placeholder="Not given" testId="customer-dob-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Tier</label>
        <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={form.tier_id} onChange={e => set("tier_id", e.target.value)} data-testid="customer-tier-select">
          <option value="">None</option>
          {tiers.map(t => <option value={t.id} key={t.id}>{t.name} · {t.discount_percent}%</option>)}
        </select>
      </div>
    </div>
  </Modal>;
}

function PointsDialog({ customer, onAdjust, onClose }) {
  const [points, setPoints] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const value = Number(points);
    if (!Number.isInteger(value) || value === 0) return toast.error("Enter a non-zero whole number");
    if (!reason.trim()) return toast.error("A reason is required for a manual adjustment");
    setSaving(true);
    const result = await onAdjust(customer.id, value, reason.trim());
    setSaving(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(`${value > 0 ? "+" : ""}${value} points · ${customer.name}`);
    onClose();
  };

  return <Modal title={`Adjust points · ${customer.name}`} onClose={onClose} testId="loyalty-adjust-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-loyalty-adjust-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-loyalty-adjust-button">Apply adjustment</Button></>}>
    <div className="space-y-4">
      <div className="bg-muted p-3 rounded-lg text-body text-muted-foreground">
        Current Balance: <strong className="text-foreground">{customer.loyalty_points ?? 0} points</strong>. Negative deducts.
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Points</label>
        <Input type="number" value={points} onChange={e => setPoints(e.target.value)} data-testid="loyalty-points-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Reason</label>
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Goodwill after a long wait" data-testid="loyalty-reason-input"/>
      </div>
    </div>
  </Modal>;
}

function PaymentDialog({ account, onReceive, onClose }) {
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("Cash");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) return toast.error("Enter an amount greater than zero");
    if (value > Number(account.balance)) {
      return toast.error(`That is more than the ${inr(account.balance)} outstanding`);
    }
    setSaving(true);
    const result = await onReceive(account.id, { amount: value, payment_mode: mode, note: note || null });
    setSaving(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(`${inr(value)} received from ${account.customer_name}`);
    onClose();
  };
  return <Modal title={`Receive payment · ${account.customer_name}`} onClose={onClose} testId="khata-payment-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="record-khata-payment-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="record-khata-payment-button">Record payment</Button></>}>
    <div className="space-y-4">
      <div className="bg-warning/10 border border-warning/20 text-warning p-3 rounded-lg text-body font-bold flex justify-between">
        <span>Outstanding</span>
        <span>{inr(account.balance)}</span>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Amount</label>
        <Input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} data-testid="khata-payment-amount-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Mode</label>
        <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={mode} onChange={e => setMode(e.target.value)} data-testid="khata-payment-mode-select">
          {["Cash", "UPI", "Card", "Bank transfer"].map(m => <option key={m}>{m}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Reference</label>
        <Input value={note} onChange={e => setNote(e.target.value)} data-testid="khata-payment-reference-input"/>
      </div>
    </div>
  </Modal>;
}

function KhataDialog({ customer, choices = [], onOpen, onClose }) {
  const [limit, setLimit] = useState("");
  const [company, setCompany] = useState("");
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const chosen = customer || choices.find(entry => String(entry.id) === pick) || null;

  const submit = async () => {
    if (!chosen) return toast.error("Pick the customer this account is for");
    if (limit !== "" && (!Number.isFinite(Number(limit)) || Number(limit) < 0)) {
      return toast.error("A credit limit is zero or more, or blank for no limit");
    }
    setBusy(true);
    const result = await onOpen({
      customer_id: chosen.id,
      credit_limit: limit === "" ? null : Number(limit),
      company_name: company.trim() || null,
    });
    setBusy(false);
    if (!result.ok) return toast.error(result.error);
    toast.success(`Khata opened for ${chosen.name}`);
    onClose();
  };
  return <Modal title={customer ? `Open khata · ${customer.name}` : "Open khata account"} onClose={onClose} testId="open-khata-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-khata-account-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={busy} data-testid="save-khata-account-button">Open account</Button></>}>
    <div className="space-y-4">
      <p className="text-body text-muted-foreground">Charges from a closed order add to this tab; payments received reduce it.</p>

      {!customer && (
        <div className="space-y-1.5">
          <label className="text-meta font-medium text-muted-foreground">Customer</label>
          <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={pick} onChange={e => setPick(e.target.value)} data-testid="khata-customer-select">
            <option value="">Select a customer…</option>
            {choices.map(entry => <option value={entry.id} key={entry.id}>{entry.name} · {entry.phone}</option>)}
          </select>
        </div>
      )}
      {!customer && !choices.length && (
        <p className="text-meta text-warning bg-warning-subtle p-2 rounded border border-warning/40" data-testid="khata-no-customers">
          Every customer on file already has a khata account. Add the customer first on the Customer profiles tab.
        </p>
      )}
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Credit limit (₹)</label>
        <Input type="number" min="0" step="0.01" value={limit} onChange={e => setLimit(e.target.value)} placeholder="Blank for no limit" data-testid="khata-credit-limit-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Company name</label>
        <Input value={company} onChange={e => setCompany(e.target.value)} placeholder="Optional — prints on the invoice" data-testid="khata-company-input"/>
      </div>
    </div>
  </Modal>;
}

function CampaignDialog({ onCreate, onClose }) {
  const [form, setForm] = useState({ name: "", segment: "all", body: "", channel: "sms" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(c => ({ ...c, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.body.trim()) return toast.error("Name and message are required");
    setSaving(true);
    const result = await onCreate({ ...form, name: form.name.trim(), body: form.body.trim() });
    setSaving(false);
    if (!result.ok) return toast.error(result.error);
    toast.success("Campaign saved as a draft");
    onClose();
  };
  return <Modal title="Create campaign" onClose={onClose} testId="campaign-modal" footer={<><Button variant="outline" onClick={onClose} data-testid="save-campaign-button-cancel">Cancel</Button><Button variant="action" onClick={submit} disabled={saving} data-testid="save-campaign-button">Save draft</Button></>}>
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Name</label>
        <Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="campaign-name-input"/>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Audience</label>
        <select className="w-full h-9 px-3 border rounded-md bg-card text-body focus:outline-none focus:ring-2 focus:ring-primary/50" value={form.segment} onChange={e => set("segment", e.target.value)} data-testid="campaign-segment-select">
          {SEGMENTS.map(([id, label]) => <option value={id} key={id}>{label}</option>)}
        </select>
      </div>
      <div className="space-y-1.5">
        <label className="text-meta font-medium text-muted-foreground">Message</label>
        <textarea className="w-full min-h-[100px] p-3 text-body rounded-md border bg-card focus:outline-none focus:ring-2 focus:ring-primary/50" value={form.body} onChange={e => set("body", e.target.value)} rows={4} data-testid="campaign-message-input"/>
      </div>
    </div>
  </Modal>;
}

function OrdersDialog({ customer, orders, onClose }) {
  return <Modal title={`Orders · ${customer.name}`} onClose={onClose} testId="customer-orders-modal">
    {orders.length ? <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
      {orders.map((order, i) => (
        <div className="flex items-center justify-between p-3 bg-muted/30 border rounded-xl" key={order.id} data-testid={`customer-order-${i}`}>
          <div className="flex flex-col">
            <span className="text-meta font-medium text-muted-foreground">{(order.order_date || order.created_at || "").slice(0, 10)}</span>
            <strong className="text-body font-bold mt-0.5">#{order.order_number}</strong>
            <span className="text-meta capitalize text-muted-foreground">{order.status.replace(/_/g, ' ')}</span>
          </div>
          <b className="text-lede font-bold text-foreground">{inr(order.total)}</b>
        </div>
      ))}
    </div> : <EmptyState density="inline" title="No orders" message="No orders yet." testId="customer-orders-empty"/>}
  </Modal>;
}

// ---------------------------------------------------------------- page

export default function CRMKhataPage() {
  const { token } = usePOS();
  const crm = useCrm(token);
  const [tab, setTab] = useState("profiles");
  const [term, setTerm] = useState("");
  const [dialog, setDialog] = useState(null);
  const [orders, setOrders] = useState([]);

  const pendingByCampaign = useMemo(() => {
    const counts = {};
    for (const m of crm.messages) counts[m.campaign_id] = (counts[m.campaign_id] || 0) + 1;
    return counts;
  }, [crm.messages]);

  const accountFor = customerId => crm.accounts.find(a => a.customer_id === customerId);

  const showOrders = async customer => {
    const result = await crm.customerOrders(customer.id);
    if (!result.ok) return toast.error(result.error);
    setOrders(result.data);
    setDialog({ kind: "orders", customer });
  };

  const schedule = async campaign => {
    const result = await crm.sendCampaign(campaign.id, new Date().toISOString());
    if (!result.ok) return toast.error(result.error);
    toast.success(`${result.data.queued} message(s) queued — no gateway is configured, so nothing has been delivered`);
  };

  const setFeedbackStatus = async (entry, status) => {
    const result = await crm.resolveFeedback(entry.id, { status });
    if (!result.ok) return toast.error(result.error);
    toast.success(`Marked ${status}`);
  };

  /*
   * CUSTOMER INTELLIGENCE / "Customers, credit & campaigns" / a count line, under a
   * topbar reading CRM. The counts survive on the panels they describe — each tab
   * states its own — rather than in a header that shows all four at once and is
   * right about whichever tab happens to be open (§2.2).
   */
  return <PageShell
    testId="crm-khata-page"
    actions={<Button variant="action" size="sm" className="font-semibold shadow-sm" onClick={() => setDialog({ kind: "customer" })} data-testid="add-customer-button">
      <Plus className="w-4 h-4 mr-snug"/>Add customer
    </Button>}
  >
    {crm.error && (
      <div className="bg-destructive/10 text-destructive border border-destructive/20 p-base rounded-lg flex items-start gap-snug shrink-0" role="alert" data-testid="crm-error">
        <TriangleAlert className="w-4 h-4 shrink-0 mt-hair"/>
        <span className="text-body font-semibold">{crm.error}</span>
      </div>
    )}

    <div className="flex gap-snug overflow-x-auto scrollbar-hide border-b shrink-0" data-testid="module-tabs">
      {tabs.map(([id, label, Icon]) => {
        const isActive = tab === id;
        return (
          <button
            key={id}
            className={`flex items-center gap-snug px-base py-snug -mb-px text-body font-medium transition-colors border-b-2 whitespace-nowrap ${isActive ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTab(id)}
            data-testid={`crm-tab-${id}-button`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        )
      })}
    </div>

    {tab === "profiles" && <div className="space-y-base" data-testid="customer-profiles-panel">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-snug">
        <div className="flex items-baseline gap-snug flex-wrap">
          <h3 className="text-title font-semibold text-foreground">Customer profiles</h3>
          <span className="text-meta font-medium text-muted-foreground">Customers</span>
          <span className="rounded-full bg-muted px-snug py-hair text-meta font-medium text-muted-foreground tabular-nums" data-testid="customer-profiles-count">{crm.customers.length}</span>
        </div>
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-snug top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-page h-9 text-meta"
            value={term}
            onChange={e => { setTerm(e.target.value); crm.search(e.target.value); }}
            placeholder="Search name or phone…"
            data-testid="customer-search-input"
          />
        </div>
      </div>

      {/*
        * A card grid for a customer list is a phone-book printed as postcards. Each
        * card carried a 48px monogram of two letters, a bordered sub-panel around two
        * facts, and three full-width buttons — roughly 190px of screen per customer,
        * three across. A restaurant with 400 customers cannot scan that. This is the
        * StockTable row shape: name and phone in one column, the two numbers in their
        * own tabular-nums columns, actions on the right (§2.2, §21).
        */}
      {crm.customers.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden shadow-sm" data-testid="customer-profiles-table">
          <div className="overflow-x-auto scroll-contain-x">
            <table className="w-full text-body text-left">
              <thead className="text-meta text-muted-foreground bg-muted/50">
                <tr>
                  <th className="px-base py-snug font-medium sticky left-0 z-[1] bg-muted md:static md:bg-transparent" data-testid="crm-name-pinned">Customer</th>
                  <th className="px-base py-snug font-medium text-right hidden sm:table-cell">Visits</th>
                  <th className="px-base py-snug font-medium text-right">Loyalty</th>
                  <th className="px-base py-snug font-medium">Tier</th>
                  <th className="px-base py-snug font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {crm.customers.map((customer, index) => (
                  <tr key={customer.id} className="hover:bg-muted/30 transition-colors" data-testid={`crm-customer-${index}`}>
                    <td className="px-base py-snug sticky left-0 z-[1] bg-card md:static max-w-[11rem] md:max-w-none">
                      <div className="flex items-center gap-snug min-w-0">
                        <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-micro shrink-0">
                          {initials(customer.name)}
                        </span>
                        <div className="flex flex-col min-w-0">
                          <strong className="font-semibold text-foreground truncate">{customer.name}</strong>
                          <small className="text-micro text-muted-foreground tabular-nums">{customer.phone}</small>
                        </div>
                      </div>
                    </td>
                    <td className="px-base py-snug text-right text-muted-foreground tabular-nums hidden sm:table-cell">{customer.visit_count ?? 0}</td>
                    <td className="px-base py-snug text-right font-medium tabular-nums text-primary whitespace-nowrap">
                      {customer.loyalty_points ?? 0} <span className="text-meta text-muted-foreground ml-hair">pts</span>
                    </td>
                    <td className="px-base py-snug">
                      <select
                        className="bg-transparent text-body font-medium focus:outline-none max-w-[160px] text-foreground"
                        value={customer.tier_id ?? ""}
                        onChange={async e => {
                          const result = await crm.setTier(customer.id, e.target.value ? Number(e.target.value) : null);
                          if (!result.ok) toast.error(result.error);
                        }}
                        data-testid={`crm-customer-${index}-tier-select`}
                        aria-label={`Tier for ${customer.name}`}
                      >
                        <option value="">No tier</option>
                        {crm.tiers.map(t => <option value={t.id} key={t.id}>{t.name} · {t.discount_percent}%</option>)}
                      </select>
                    </td>
                    <td className="px-base py-snug">
                      <div className="flex items-center justify-end gap-tight">
                        <Button variant="outline" size="sm" className="h-8 px-snug text-micro font-bold" onClick={() => setDialog({ kind: "points", customer })} data-testid={`crm-customer-${index}-points-button`}>Points</Button>
                        <Button variant="outline" size="sm" className="h-8 px-snug text-micro font-bold" onClick={() => showOrders(customer)} data-testid={`crm-customer-${index}-orders-button`}>Orders</Button>
                        {accountFor(customer.id)
                          ? <Button variant="secondary" size="sm" className="h-8 px-snug text-micro font-bold bg-muted/50 text-muted-foreground" disabled title="This customer already has a khata account" data-testid={`crm-customer-${index}-khata-open`}>On khata</Button>
                          : <Button variant="secondary" size="sm" className="h-8 px-snug text-micro font-bold" onClick={() => setDialog({ kind: "khata", customer })} data-testid={`crm-customer-${index}-khata-button`}>Open khata</Button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!crm.customers.length && !crm.loading && !crm.error && <EmptyState icon={UserRoundSearch} title="No matches" message="No customers match." testId="crm-customers-empty"/>}
    </div>}

    {tab === "khata" && <div className="space-y-base" data-testid="khata-accounts-panel">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-snug">
        <div>
          <h3 className="text-title font-semibold text-foreground">Khata &amp; corporate</h3>
          <p className="text-meta text-muted-foreground">{crm.accounts.length} account(s) on credit</p>
        </div>
        <Button variant="secondary" size="sm" className="font-semibold shadow-sm whitespace-nowrap" onClick={() => setDialog({ kind: "khata", customer: null })} data-testid="open-khata-account-button"><Plus className="w-4 h-4 mr-snug"/>Open khata account</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-snug">
        {crm.accounts.map((account, index) => {
          const isNegative = Number(account.balance) > 0;
          return (
            <article key={account.id} className={`bg-card border rounded-lg px-base py-snug shadow-sm flex flex-col gap-snug h-full ${isNegative ? 'border-warning/30 bg-warning/10' : ''}`} data-testid={`khata-account-${index}`}>
              <header className="flex gap-snug items-center min-w-0">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-micro shrink-0 ${isNegative ? 'bg-warning-subtle text-warning' : 'bg-primary/10 text-primary'}`}>
                  {initials(account.company_name || account.customer_name)}
                </span>
                <div className="flex-1 min-w-0">
                  <strong className="block text-lede font-semibold text-foreground truncate">{account.company_name || account.customer_name}</strong>
                  <small className="text-micro text-muted-foreground block truncate tabular-nums">{account.customer_phone}</small>
                </div>
              </header>
              <div className="flex items-baseline justify-between gap-snug">
                <span className="flex items-baseline gap-tight">
                  <span className="text-meta font-medium text-muted-foreground">Balance</span>
                  <b className={`${MONEY_LINE} ${isNegative ? "text-warning" : "text-success"}`}>{inr(account.balance)}</b>
                </span>
                <span className="flex items-baseline gap-tight">
                  <span className="text-meta font-medium text-muted-foreground">Limit</span>
                  <i className={`${MONEY_CAPTION} text-muted-foreground not-italic`}>{account.credit_limit === null ? "No limit" : inr(account.credit_limit)}</i>
                </span>
              </div>
              <Button
                variant={isNegative ? "default" : "secondary"}
                size="sm"
                className={`w-full mt-auto font-bold ${isNegative ? '' : 'opacity-50'}`}
                onClick={() => setDialog({ kind: "payment", account })}
                disabled={Number(account.balance) <= 0}
                title={Number(account.balance) <= 0 ? "Nothing outstanding" : "Receive payment"}
                data-testid={`khata-account-${index}-receive-button`}
              >Receive payment</Button>
            </article>
          )
        })}
      </div>
      {!crm.accounts.length && !crm.loading && <EmptyState icon={WalletCards} title="No accounts" message="No khata accounts yet. Use “Open khata account” above, or the “Open khata” button on a row in the Customer profiles tab." testId="khata-empty"/>}
    </div>}

    {tab === "segments" && <div className="space-y-loose" data-testid="customer-segments-panel">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-snug">
        <div>
          <h3 className="text-title font-semibold text-foreground">Campaigns</h3>
          <p className="text-meta text-muted-foreground">{crm.messages.length} message(s) queued and undelivered</p>
        </div>
        <Button variant="secondary" size="sm" className="font-semibold shadow-sm whitespace-nowrap" onClick={() => setDialog({ kind: "campaign" })} data-testid="create-campaign-button"><Plus className="w-4 h-4 mr-snug"/>Create campaign</Button>
      </div>

      <div className="bg-warning/10 border border-warning/20 px-base py-snug rounded-lg flex items-start gap-snug" data-testid="gateway-notice" role="status">
        <Send className="w-4 h-4 shrink-0 mt-hair text-warning"/>
        <p className="text-body font-semibold text-warning">No messaging gateway is configured. Campaigns resolve their audience and queue messages, but nothing is delivered until a gateway exists.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-snug">
        {crm.campaigns.map((campaign, index) => (
          <article className="flex gap-snug justify-between items-center bg-card border rounded-lg px-base py-snug shadow-sm" key={campaign.id} data-testid={`campaign-${index}`}>
            <div className="flex items-center gap-snug min-w-0">
              <span className="w-8 h-8 rounded-md bg-info-subtle text-info flex items-center justify-center font-medium text-meta shrink-0">
                {campaign.channel === "sms" ? "SMS" : campaign.channel.slice(0, 3)}
              </span>
              <div className="flex flex-col min-w-0">
                <strong className="text-lede font-semibold text-foreground truncate">{campaign.name}</strong>
                <small className="text-micro text-muted-foreground truncate">
                  <span className="capitalize">{campaign.segment}</span> · <span className="tabular-nums">{campaign.recipient_count}</span> recipients · <span className="tabular-nums">{pendingByCampaign[campaign.id] || 0}</span> pending
                </small>
              </div>
            </div>

            <div className="flex items-center gap-snug shrink-0">
              <b className={`text-meta font-medium px-snug py-hair rounded-md ${campaign.status === 'draft' ? 'bg-muted text-muted-foreground' : 'bg-warning-subtle text-warning'}`}>{campaign.status}</b>
              {campaign.status === "draft" && <Button variant="action" size="sm" onClick={() => schedule(campaign)} data-testid={`campaign-${index}-schedule-button`}>Queue</Button>}
              {campaign.status === "scheduled" && <Button size="sm" variant="destructive" onClick={async () => { const r = await crm.stopCampaign(campaign.id); if (!r.ok) toast.error(r.error); else toast.success("Campaign cancelled"); }} data-testid={`campaign-${index}-cancel-button`}>Cancel</Button>}
            </div>
          </article>
        ))}
      </div>
      {!crm.campaigns.length && !crm.loading && <EmptyState icon={MessageSquareText} title="No campaigns" message="No campaigns yet." testId="campaigns-empty"/>}
    </div>}

    {tab === "feedback" && <div className="space-y-loose" data-testid="feedback-loop-panel">
      {crm.summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 border rounded-lg bg-card divide-y sm:divide-y-0 sm:divide-x md:divide-y-0" data-testid="feedback-summary">
          <div className="p-base flex flex-col gap-hair">
            <small className="text-meta font-medium text-muted-foreground">Open</small>
            <strong className="text-display font-bold text-danger tabular-nums" data-testid="feedback-count-open">{crm.summary.by_status?.open ?? 0}</strong>
          </div>
          <div className="p-base flex flex-col gap-hair">
            <small className="text-meta font-medium text-muted-foreground">Contacted</small>
            <strong className="text-display font-bold text-warning tabular-nums" data-testid="feedback-count-contacted">{crm.summary.by_status?.contacted ?? 0}</strong>
          </div>
          <div className="p-base flex flex-col gap-hair">
            <small className="text-meta font-medium text-muted-foreground">Resolved</small>
            <strong className="text-display font-bold text-success tabular-nums" data-testid="feedback-count-resolved">{crm.summary.by_status?.resolved ?? 0}</strong>
          </div>
          <div className="p-base flex flex-col gap-hair">
            <small className="text-meta font-medium text-muted-foreground">Average rating</small>
            <strong className="text-display font-bold flex items-center text-primary tabular-nums">
              {crm.summary.average_rating ?? "—"}
              {crm.summary.average_rating && <Star className="w-4 h-4 fill-primary ml-tight"/>}
            </strong>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-snug">
        {crm.feedback.map((entry, index) => (
          <article className="bg-card border rounded-lg px-base py-snug shadow-sm flex flex-col gap-snug h-full" key={entry.id} data-testid={`feedback-${index}`}>
            <header className="flex justify-between items-baseline gap-snug">
              <strong className="text-lede font-semibold text-foreground truncate">{entry.customer_name || "Walk-in"}</strong>
              <span className="flex items-center gap-tight shrink-0">
                <span className={`text-meta font-medium px-snug py-hair rounded-md ${entry.status === 'open' ? 'bg-danger-subtle text-danger' : entry.status === 'contacted' ? 'bg-warning-subtle text-warning' : 'bg-success-subtle text-success'}`}>
                  {entry.status}
                </span>
                <span className="flex items-center gap-hair bg-muted px-snug py-hair rounded-md text-foreground font-bold text-micro tabular-nums">
                  {entry.rating ? <><Star className="w-3 h-3 fill-foreground"/> {entry.rating}</> : "—"}
                </span>
              </span>
            </header>
            <p className="text-body text-muted-foreground italic flex-1">
              "{entry.comment || "No comment provided."}"
            </p>

            <div className="flex gap-snug mt-auto">
              {entry.status === "open" && <Button variant="outline" size="sm" className="flex-1 font-bold text-warning hover:text-warning hover:bg-warning-subtle" onClick={() => setFeedbackStatus(entry, "contacted")} data-testid={`feedback-${index}-contact-button`}>Mark contacted</Button>}
              {entry.status !== "resolved" && entry.status !== "dismissed" && <Button variant="action" size="sm" className="flex-1 font-bold" onClick={() => setFeedbackStatus(entry, "resolved")} data-testid={`feedback-${index}-resolve-button`}><CheckCircle2 className="w-4 h-4 mr-tight"/>Resolve</Button>}
            </div>
          </article>
        ))}
      </div>
      {!crm.feedback.length && !crm.loading && <EmptyState icon={BellRing} title="No feedback" message="No feedback recorded." testId="feedback-empty"/>}
    </div>}

    {dialog?.kind === "customer" && (
      <CustomerDialog
        tiers={crm.tiers}
        onSave={async (body, tierId) => {
          const created = await crm.addCustomer(body);
          if (created.ok && tierId) await crm.setTier(created.data.id, tierId);
          return created;
        }}
        onClose={() => setDialog(null)}
      />
    )}
    {dialog?.kind === "points" && <PointsDialog customer={dialog.customer} onAdjust={crm.adjustPoints} onClose={() => setDialog(null)}/>}
    {dialog?.kind === "payment" && <PaymentDialog account={dialog.account} onReceive={crm.receivePayment} onClose={() => setDialog(null)}/>}
    {dialog?.kind === "khata" && (
      <KhataDialog
        customer={dialog.customer}
        choices={crm.customers.filter(entry => !accountFor(entry.id))}
        onOpen={crm.openKhata}
        onClose={() => setDialog(null)}
      />
    )}
    {dialog?.kind === "campaign" && <CampaignDialog onCreate={crm.addCampaign} onClose={() => setDialog(null)}/>}
    {dialog?.kind === "orders" && <OrdersDialog customer={dialog.customer} orders={orders} onClose={() => setDialog(null)}/>}
  </PageShell>;
}
